import Button from "@/components/Button";
import { cn } from "@/lib/utils";
import { showModal } from "@/redux/message/messageSlice";
import { AppDispatch } from "@/redux/store";
import { IOS_CLUB_IMPORT_APP_URL } from "@/settings";
import { useTranslation } from "@repo/shared/i18n";
import { ChevronLeft } from "lucide-react";
import { useDispatch } from "react-redux";

import { CompletedStepIcon } from "../CompletedStepIcon";
import { CopyLinkButton } from "./CopyLinkButton";

const ChevronRight = () => (
  <div className="rotate-180">
    <ChevronLeft className="w-6 h-6" />
  </div>
);

const ExternalLinkIcon = () => (
  <svg
    width="12"
    height="12"
    viewBox="0 0 12 12"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
  >
    <path
      d="M9.5 2.5L2.5 9.5M9.5 2.5H6.5M9.5 2.5V5.5"
      stroke="currentColor"
      strokeWidth="1.5"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

interface StepProps {
  stepNumber: number;
  completed: boolean;
  description: string | React.ReactNode;
  buttons?: Array<{
    label: string | React.ReactNode;
    variant: "primary" | "secondary" | "disabled";
    onClick: () => void;
    hasExternalIcon?: boolean;
  }>;
  buttonAlignment?: "horizontal" | "vertical";
  showButtons?: boolean;
  enabled?: boolean;
}

const StepCard = ({
  stepNumber,
  completed,
  enabled,
  description,
  buttons,
  buttonAlignment = "horizontal",
}: StepProps) => {
  const { t } = useTranslation("clubImport");
  return (
    <div
      className={cn(
        "bg-panel-background border border-new-gray-700 rounded-[15px] p-4 flex flex-col gap-2.5",
        (completed || !enabled) && "opacity-50",
      )}
    >
      {/* Header */}
      <div className="flex items-center gap-2.5">
        <h3 className="text-white text-sm font-bold">
          {t("step", { n: stepNumber })}
        </h3>
        <CompletedStepIcon completed={completed} />
      </div>

      {/* Description */}
      <div className="text-white text-sm leading-5">{description}</div>

      {/* Buttons */}
      {buttons && !completed && enabled && buttons.length > 0 && (
        <div
          className={cn(
            "flex flex-row gap-5",
            buttonAlignment === "vertical" && "flex-col gap-2.5",
          )}
        >
          {buttons.map((button, index) => (
            <div key={index}>
              <Button
                variant={button.variant}
                label={
                  button.hasExternalIcon ? (
                    <span className="flex items-center gap-1">
                      {button.label}
                      <span className="text-white">
                        <ExternalLinkIcon />
                      </span>
                    </span>
                  ) : (
                    button.label
                  )
                }
                onClick={button.onClick}
              />
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export const MainSection = ({
  step1Completed,
  step2Completed,
  step3Completed,
  step4Completed,
  loggedIn,
  onSetupExtensionClick,
  onClubImportGuideClick,
  iosExtensionInstalled,
}: {
  step1Completed: boolean;
  step2Completed: boolean;
  step3Completed: boolean;
  step4Completed: boolean;
  loggedIn: boolean;
  onSetupExtensionClick: () => void;
  onClubImportGuideClick: () => void;
  iosExtensionInstalled: boolean;
}) => {
  const { t } = useTranslation("clubImport");
  const dispatch = useDispatch<AppDispatch>();

  return (
    <div className="flex flex-col gap-[15px] bg-panel-base p-4">
      <h1 className="text-white text-xl font-bold">{t("mobile.title")}</h1>
      <StepCard
        stepNumber={1}
        completed={step1Completed}
        enabled={!step1Completed}
        description={
          step1Completed ? (
            t("mobile.main.step1.completed")
          ) : iosExtensionInstalled ? (
            <div className="flex flex-col gap-y-2.5 text-dark-secondary">
              <p>{t("mobile.main.step1.installed")}</p>
              <img
                src={`${import.meta.env.VITE_APP_ASSETS_BASE_URL}/import-tutorial/mobile/safari-from-extension.png`}
                alt={t("mobile.main.step1.imageAlt")}
                className="h-[80px] mx-auto my-3"
              />
              <div className="flex flex-col gap-y-5">
                <p>{t("mobile.main.step1.safariOnly")}</p>
                <div>
                  <CopyLinkButton />
                </div>
              </div>
            </div>
          ) : (
            <div className="flex flex-col">
              <img
                src={`${import.meta.env.VITE_APP_ASSETS_BASE_URL}/import-tutorial/mobile/safari-needed.png`}
                alt={t("mobile.main.step1.imageAlt")}
                className="h-[80px] flex items-center justify-center mx-auto mb-[22px] my-3"
              />
              <p className="mb-5">{t("mobile.main.step1.safariOnly")}</p>
              <div>
                <CopyLinkButton />
              </div>
            </div>
          )
        }
      />

      <StepCard
        stepNumber={2}
        completed={step2Completed}
        enabled={!step2Completed && step1Completed}
        description={t("mobile.main.step2.text")}
        buttons={[
          {
            label: t("mobile.main.step2.cta"),
            variant: loggedIn ? "disabled" : "primary",
            onClick: () => dispatch(showModal({ modalType: "login-required" })),
          },
        ]}
      />

      <StepCard
        stepNumber={3}
        completed={step3Completed}
        enabled={!step3Completed && step2Completed && step1Completed}
        description={
          <>
            {t("mobile.main.step3.text")}{" "}
            <span
              className="underline cursor-pointer"
              onClick={() =>
                dispatch(showModal({ modalType: "ios-import-popup" }))
              }
            >
              {t("mobile.main.step3.learnMore")}
            </span>
          </>
        }
        buttons={[
          {
            label: t("mobile.main.step3.cta"),
            variant: "primary",
            onClick: () => window.open(IOS_CLUB_IMPORT_APP_URL, "_blank"),
            hasExternalIcon: true,
          },
        ]}
      />

      <StepCard
        stepNumber={4}
        enabled={
          step1Completed && step2Completed && step3Completed && !step4Completed
        }
        completed={step4Completed}
        description={t("mobile.main.step4.text")}
        buttons={[
          {
            label: t("mobile.main.step4.cta"),
            variant: "primary",
            onClick: onSetupExtensionClick,
            hasExternalIcon: true,
          },
        ]}
      />

      <StepCard
        stepNumber={5}
        completed={false}
        description={
          step1Completed && step2Completed && step3Completed && step4Completed
            ? t("mobile.main.step5.ready")
            : t("mobile.main.step5.text")
        }
        enabled={
          step1Completed && step2Completed && step3Completed && step4Completed
        }
        buttonAlignment="vertical"
        buttons={[
          {
            label: (
              <span className="flex items-center gap-1">
                {t("mobile.main.step5.cta")}
                <ChevronRight />
              </span>
            ),
            variant: "primary" as const,
            onClick: onClubImportGuideClick,
          },
        ]}
      />
    </div>
  );
};
